/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  StationPolicy,
  StreamingRestrictionsInternal
} from "@tivocorp/data-domain-lib";

import { InHomeLocationModel } from "../../device";
import { PlaybackContentType, PlaybackRestrictionType } from "../enums";
import type { IRestriction } from "../interfaces";
import type { TInHomeRestrictionParams } from "../types";

/**
 * Location-based restriction check.
 *
 * Reads the current `InHomeLocationModel` record and maps it to a
 * `PlaybackRestrictionType`. This impl only consults the InHomeLocation
 * signal itself. Any policy-level override (e.g. `StationPolicy`'s
 * `outOfHomeStreaming` flag) is applied by the caller — see
 * `RestrictionManager.checkRestriction`.
 */
export class InHomeLocationRestrictionImpl implements IRestriction<TInHomeRestrictionParams> {
  private getInHomeRestrictionStatus(): PlaybackRestrictionType {
    const inHomeLocation = InHomeLocationModel.get();

    if (inHomeLocation === null) {
      return PlaybackRestrictionType.INHOME_LOCATION_UNREACHABLE;
    }

    if (
      inHomeLocation.inHome === null &&
      inHomeLocation.inRegion === null &&
      inHomeLocation.inBlacklist === null
    ) {
      return PlaybackRestrictionType.INHOME_LOCATION_ERROR;
    }

    return PlaybackRestrictionType.NO_RESTRICTION;
  }

  private getFilteredStationPolicies(
    policyInfo: Array<StationPolicy | undefined>,
    playbackContentType: PlaybackContentType
  ): StreamingRestrictionsInternal[] {
    const extractRestriction = (
      policy: StationPolicy | undefined
    ): StreamingRestrictionsInternal | undefined => {
      if (policy === undefined || policy === null) {
        return undefined;
      }
      const linearStationRules = policy.linearStationRules;
      if (linearStationRules === undefined || linearStationRules === null) {
        return undefined;
      }
      const streamingRules = linearStationRules.streamingRules;
      if (streamingRules === undefined || streamingRules === null) {
        return undefined;
      }

      switch (playbackContentType) {
        case PlaybackContentType.CATCHUP_BEGINNING:
        case PlaybackContentType.CATCHUP_RESUME:
          return streamingRules.cuStreamingRestrictions;

        case PlaybackContentType.STARTOVER_BEGINNING:
        case PlaybackContentType.STARTOVER_RESUME:
          return streamingRules.soStreamingRestrictions;

        default:
          return streamingRules.linearStreamingRestrictions;
      }
    };

    const filteredPolicies: StreamingRestrictionsInternal[] = [];
    for (let index = 0; index < policyInfo.length; index += 1) {
      const restriction = extractRestriction(policyInfo[index]);
      if (restriction !== undefined && restriction !== null) {
        filteredPolicies.push(restriction);
      }
    }
    return filteredPolicies;
  }

  private isBlacklist(): boolean {
    const inHomeLocation = InHomeLocationModel.get();
    if (inHomeLocation && inHomeLocation.inBlacklist) {
      return inHomeLocation.inBlacklist;
    }
    return false;
  }

  private isOutOfHome(
    playbackContentType: PlaybackContentType,
    policyInfo?: Array<StationPolicy | undefined>
  ): boolean {
    const inHomeLocation = InHomeLocationModel.get();

    if (
      playbackContentType === PlaybackContentType.VOD &&
      inHomeLocation &&
      !inHomeLocation.inHome
    ) {
      return !inHomeLocation.inHome;
    }

    if (
      (inHomeLocation && inHomeLocation.inHome) ||
      (policyInfo && !policyInfo.length)
    )
      return false;

    const filteredPolicies: StreamingRestrictionsInternal[] =
      this.getFilteredStationPolicies(
        policyInfo || ({} as Array<StationPolicy | undefined>),
        playbackContentType
      );

    for (let _index = 0; _index < filteredPolicies.length; _index += 1) {
      const streamingRestrictions = filteredPolicies[_index];
      if (
        streamingRestrictions &&
        streamingRestrictions.outOfHomeStreaming &&
        streamingRestrictions.outOfHomeStreaming === "blocked"
      ) {
        return true;
      }
    }
    return false;
  }

  private isOutOfRegion(): boolean {
    const inHomeLocation = InHomeLocationModel.get();
    if (inHomeLocation && !inHomeLocation.inRegion) {
      return !inHomeLocation.inRegion;
    }
    return false;
  }

  public async checkRestriction(
    data: TInHomeRestrictionParams
  ): Promise<PlaybackRestrictionType> {
    const serviceStatusRestriction = this.getInHomeRestrictionStatus();

    if (serviceStatusRestriction !== PlaybackRestrictionType.NO_RESTRICTION) {
      return serviceStatusRestriction;
    }

    if (this.isBlacklist()) return PlaybackRestrictionType.BLACKLISTED;

    if (this.isOutOfRegion()) return PlaybackRestrictionType.OUT_OF_REGION;

    if (this.isOutOfHome(data.playbackContentType, data.stationPolicy))
      return PlaybackRestrictionType.OUT_OF_HOME;

    return PlaybackRestrictionType.NO_RESTRICTION;
  }
}
